import type { ApiAdmininsightsperformancesGetCollectionParams } from '@r/api/generated/client.schemas.ts';
import { useApiAdmininsightsperformancesGetCollection } from '@r/api/generated/client.ts';
import {
  type FormattedDataForRendering,
  BarChartBloc
} from '@r/components/kpi/points/bar-chart-bloc.tsx';
import { DataBrickSkeleton } from '@r/components/kpi/skeleton/data-brick-skeleton.tsx';
import { ChartLineIcon } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';
import { BarChartSkeleton } from '@r/components/kpi/skeleton/bar-chart-skeleton.tsx';
import { getDataBrick } from '@admin/components/data-brick.tsx';
import CountUp from 'react-countup';
import type { InsightPerformanceInsightPerformanceReadModel } from '@r/api/generated/client.schemas.ts';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useState } from 'react';
import { CompanyRankingList } from '@admin/components/company/company-ranking-list.tsx';
import type { Option } from '@r/components/kpi/filters/select-tag-input.tsx';
import { KpiFilterBar } from '@r/components/kpi/filters/kpi-filter-bar.tsx';
import { companyStatuses } from '@r/types/index.ts';
import { getBarFillFromValue } from '@r/utils/chart-utils.ts';
import { useOrvalQueryErrorToast } from '@r/hooks/use-orval-error-toast.ts';

type PerformancesInsight = InsightPerformanceInsightPerformanceReadModel;

export const PerformancesInsights = () => {
  const { t } = useTranslation();

  let formattedPointsPerMonth: FormattedDataForRendering[] = [];
  let totalPeriodPerformance: number | null = null;
  let averageAmountByUsers: number | null = null;

  const [companyStatus, setCompanyStatusFilterValue] = useState<Option[]>([
    { id: 'company.status.registered', name: t('company.status.registered') }
  ]);
  const [companyTypologies, setCompanyTypologiesFilterValue] = useState<Option[]>([]);
  const [selectedSalesIds, setSelectedSalesIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ from?: string; to?: string }>({});

  const statusIds = companyStatus
    .map((status) => String(status.id))
    .filter((id): id is (typeof companyStatuses)[number] =>
      companyStatuses.includes(id as (typeof companyStatuses)[number])
    );

  const typologyNames = companyTypologies.map((typology) => String(typology.id));
  const performancesParams: ApiAdmininsightsperformancesGetCollectionParams = {
    ...(statusIds.length > 0 ? { 'status[]': statusIds } : {}),
    ...(typologyNames.length > 0 ? { 'typology[]': typologyNames } : {}),
    ...(selectedSalesIds.length > 0 ? { 'sales[]': selectedSalesIds } : {}),
    ...(dateRange.from ? { from: dateRange.from } : {}),
    ...(dateRange.to ? { to: dateRange.to } : {})
  };
  const performanceInsightsQuery = useApiAdmininsightsperformancesGetCollection(performancesParams);
  useOrvalQueryErrorToast(performanceInsightsQuery, t('toast.error.label'));

  const processPerformancesPerMonthResults = (
    performanceInsights: PerformancesInsight
  ): FormattedDataForRendering[] => {
    const totalAmountPerMonth = performanceInsights.totalAmountPerMonth;

    return totalAmountPerMonth.map((item) => {
      const monthName = format(new Date(item.year, item.month - 1), 'MMMM', { locale: fr });
      const formatedMonthName = monthName.charAt(0).toUpperCase() + monthName.slice(1);
      const totalAmount = item.value;

      return {
        date: `${formatedMonthName} ${item.year}`,
        data: totalAmount,
        fill: getBarFillFromValue(totalAmount)
      };
    });
  };

  const processTotalAmount = (performanceInsights: PerformancesInsight) => {
    const totalAmountPerMonth = performanceInsights.totalAmountPerMonth;

    return totalAmountPerMonth.reduce((acc, item) => acc + item.value, 0);
  };

  if (performanceInsightsQuery.isSuccess) {
    formattedPointsPerMonth = processPerformancesPerMonthResults(performanceInsightsQuery.data);
    totalPeriodPerformance = processTotalAmount(performanceInsightsQuery.data);
    averageAmountByUsers = performanceInsightsQuery.data.avgAmountByCompanies;
  }

  return (
    <>
      <KpiFilterBar
        onStatusChange={setCompanyStatusFilterValue}
        onTypologyChange={setCompanyTypologiesFilterValue}
        companyTypologies={companyTypologies}
        onSalesChange={setSelectedSalesIds}
        initialSelectedStatuses={[
          { id: 'company.status.registered', name: t('company.status.registered') }
        ]}
        onDateRangeChange={(range) => {
          setDateRange(range);
        }}
        dateRange={dateRange}
      />

      <div className="flex flex-wrap space-y-5 grow xl:flex-nowrap xl:space-x-5 xl:space-y-0">
        <div className="flex flex-col space-y-5 w-full xl:w-6/12 xl:min-h-full">
          <div className="w-full">
            <Card className="flex h-[450px] flex-col">
              <CardHeader className="space-y-0">
                <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
                  <ChartLineIcon />
                  <div className="text-xl font-bold">
                    {t('admin.kpi.performances.amount.twelve.months')}
                  </div>
                </CardTitle>

                {performanceInsightsQuery.isSuccess && totalPeriodPerformance !== null && (
                  <span>
                    <div className="mb-2 text-3xl font-bold text">
                      <CountUp
                        className="font-bold"
                        end={totalPeriodPerformance}
                        duration={2}
                        suffix={' €'}
                        separator={' '}
                        decimals={2}
                      />
                    </div>
                  </span>
                )}
              </CardHeader>

              <CardContent className="flex grow">
                {(performanceInsightsQuery.isLoading || performanceInsightsQuery.isError) && (
                  <BarChartSkeleton />
                )}

                {performanceInsightsQuery.isSuccess && formattedPointsPerMonth.length > 0 && (
                  <BarChartBloc
                    formattedDataPerMonth={formattedPointsPerMonth}
                    dataType={'monetary'}
                  />
                )}
              </CardContent>

              <CardFooter className="justify-center text-sm font-light text-muted-foreground">
                {t('admin.kpi.twelve.months.performances.amount')}
              </CardFooter>
            </Card>
          </div>

          <div className="w-full">
            {(performanceInsightsQuery.isLoading || performanceInsightsQuery.isError) && (
              <DataBrickSkeleton size={'large'} />
            )}

            {performanceInsightsQuery.isSuccess &&
              performanceInsightsQuery.data &&
              averageAmountByUsers !== null &&
              getDataBrick(
                t('admin.kpi.performances.average.amount'),
                <ChartLineIcon />,
                averageAmountByUsers,
                ' €',
                t('admin.kpi.performances.average.amount.info'),
                null,
                'large',
                2
              )}
          </div>
        </div>

        <div className="w-full xl:w-6/12 xl:min-h-full">
          <CompanyRankingList
            className="xl:h-full"
            orderBy="caAmount"
            direction="desc"
            filters={{
              onlyCompaniesRegistered: statusIds.includes('company.status.registered'),
              companyTypologies: companyTypologies,
              status: statusIds,
              sales: selectedSalesIds,
              dateRange: { from: dateRange.from, to: dateRange.to }
            }}
            hideImportBtn={true}
            rankingValueType={'monetary'}
          />
        </div>
      </div>
    </>
  );
};
